import { LoadingIcon } from '@/components/icons';
import PageViewLayout from '@/components/layouts/PageViewLayout/PageViewLayout';
import ConfirmationModalView from '@/components/modules/modals/ConfirmationModalView';
import PopupTerminalCard from '@/features/booking-widget/components/PopupTerminalCard/PopupTerminalCard';
import { isEmbedBundle, isEmbedMode } from '@/helpers/embed';
import { classnames } from '@/helpers/general';
import { _t } from '@/locale';
import { useEffect, useRef, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import useValidateBokadirektPay from './ValidateBokadirektPay.hooks';

const messages = [
  {
    headline: _t('pages.validate-bokadirekt-pay.ValidateBokadirektPay.phase1Headline'),
    supporting: _t('pages.validate-bokadirekt-pay.ValidateBokadirektPay.phase1Supporting'),
  },
  {
    headline: _t('pages.validate-bokadirekt-pay.ValidateBokadirektPay.phase2Headline'),
    supporting: _t('pages.validate-bokadirekt-pay.ValidateBokadirektPay.phase2Supporting'),
  },
  {
    headline: _t('pages.validate-bokadirekt-pay.ValidateBokadirektPay.phase3Headline'),
    supporting: _t('pages.validate-bokadirekt-pay.ValidateBokadirektPay.phase3Supporting'),
  },
];

const baseModalKey = 'pages.validate-bokadirekt-pay.ValidateBokadirektPay.backNavigationModal';

const ValidateBokadirektPay = () => {
  const { redirect, terminal } = useValidateBokadirektPay();
  const history = useHistory();
  const [framed] = useState(isEmbedMode);
  // The back guard is about the frame; the chrome is about the bundle. A Linked
  // Session and the pay popup are unframed but still run the widget bundle.
  const [inWidget] = useState(isEmbedBundle);
  const [messageIndex, setMessageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isLeavingRef = useRef(false);
  const unblockRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const t1 = setTimeout(() => setMessageIndex(1), 10_000);
    const t2 = setTimeout(() => setMessageIndex(2), 30_000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  useEffect(() => {
    // Framed, the frame shares its history with the merchant page, so the router
    // cancels the back press instead of a sentinel entry being pushed onto it.
    // As with holdHistory, it can only revert to entries this document created:
    // when this route is the document's first entry (the frame was reloaded on
    // the checkout, which replaced itself with it, or on this page) the back
    // press leaves unasked.
    if (framed) {
      unblockRef.current = history.block((_location, action) => {
        if (action !== 'POP') return;
        setIsModalOpen(true);
        return false;
      });
      return () => unblockRef.current?.();
    }

    // Push a sentinel entry (same document) so pressing back lands here first
    // and fires popstate — even if the page was reached via a cross-origin redirect
    // from an external payment provider (which would otherwise skip popstate entirely).
    window.history.pushState(null, '', window.location.pathname + window.location.search);

    const handlePopState = () => {
      if (isLeavingRef.current) return;
      setIsModalOpen(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [framed, history]);

  const handleLeave = () => {
    setIsModalOpen(false);
    if (framed) {
      unblockRef.current?.();
      unblockRef.current = null;
      // Lands on the step before checkout: the checkout replaced itself with this route.
      history.goBack();
      return;
    }
    isLeavingRef.current = true;
    window.history.go(-1);
  };

  const handleStay = () => {
    setIsModalOpen(false);
    if (framed) return;
    // Re-push sentinel so the next back press can be intercepted again
    window.history.pushState(null, '', window.location.pathname + window.location.search);
  };

  if (terminal) {
    return (
      <PopupTerminalCard
        title={_t(`features.bookingWidget.WidgetPayment.terminal.${terminal.kind}.title`)}
        body={
          terminal.kind === 'error' && terminal.message
            ? terminal.message
            : _t(`features.bookingWidget.WidgetPayment.terminal.${terminal.kind}.body`)
        }
        closeLabel={_t('pages.booking.pay.closeWindow')}
      />
    );
  }

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  const { headline, supporting } = messages[messageIndex];

  const content = (
    <>
      <div
        className={classnames(
          'fixed inset-0 flex flex-col items-center justify-center gap-4',
          inWidget ? 'bg-surface_mp' : 'bg-white',
        )}>
        <LoadingIcon className="h-8 w-8" />
        <div className="max-w-sm px-6 text-center">
          <p className="mb-2 text-lg font-semibold">{headline}</p>
          <p className="text-sm text-gray-500">{supporting}</p>
        </div>
      </div>
      <ConfirmationModalView
        isOpen={isModalOpen}
        title={_t(`${baseModalKey}.title`)}
        body={<p>{_t(`${baseModalKey}.body`)}</p>}
        onConfirm={handleLeave}
        onCancel={handleStay}
        confirmText={_t(`${baseModalKey}.leaveButton`)}
        cancelText={_t(`${baseModalKey}.stayButton`)}
      />
    </>
  );

  // The frame and a Linked Session have the widget's own chrome; in the pay popup
  // the marketplace NavBar's links would take the payment window into the marketplace.
  return inWidget ? content : <PageViewLayout type="subView">{content}</PageViewLayout>;
};

export default ValidateBokadirektPay;
